import { PropsWithChildren, useEffect, useRef } from 'react'
import Image from 'next/image'

import styles from './LandingHero.module.scss'

/**
 * Start page hero: the restaurant behind, the search card in front
 * and the waiter cut out on top of the card's edge. The layers drift a little
 * with the mouse and while scrolling past, unless reduced motion is set.
 */
export default function LandingHero({ children }: PropsWithChildren) {
  const heroRef = useRef<HTMLDivElement>(null)
  const backgroundRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const waiterRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const hero = heroRef.current
    const background = backgroundRef.current
    const card = cardRef.current
    const waiter = waiterRef.current

    if (!hero || !background || !card || !waiter) {
      return undefined
    }

    // Optional, so environments without matchMedia (jsdom) still render
    const reducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    )

    let targetX = 0
    let targetY = 0
    let currentX = 0
    let currentY = 0
    let frame = 0

    function render() {
      frame = 0

      if (reducedMotion?.matches) {
        background!.style.transform = ''
        card!.style.transform = ''
        waiter!.style.transform = ''
        return
      }

      currentX += (targetX - currentX) * 0.12
      currentY += (targetY - currentY) * 0.12

      // Only drifts while the hero scrolls out of view
      const scrolled = Math.min(0, hero!.getBoundingClientRect().top)
      // Offsets scale with the hero's size
      const unit = hero!.clientWidth / 1000

      background!.style.transform = `translate3d(${-currentX * 14 * unit}px, ${
        -scrolled * 0.35 - currentY * 10 * unit
      }px, 0)`
      card!.style.transform = `translate3d(${-currentX * 8 * unit}px, ${
        -scrolled * 0.12 - currentY * 6 * unit
      }px, 0)`
      waiter!.style.transform = `translate3d(${-currentX * 22 * unit}px, 0, 0)`

      if (
        Math.abs(targetX - currentX) > 0.001 ||
        Math.abs(targetY - currentY) > 0.001
      ) {
        // Keep easing towards the pointer
        frame = requestAnimationFrame(render)
      }
    }

    function requestRender() {
      if (!frame) {
        frame = requestAnimationFrame(render)
      }
    }

    function handlePointerMove(event: PointerEvent) {
      if (event.pointerType !== 'mouse') {
        return
      }

      const rect = hero!.getBoundingClientRect()
      targetX = ((event.clientX - rect.left) / rect.width) * 2 - 1
      targetY = ((event.clientY - rect.top) / rect.height) * 2 - 1
      requestRender()
    }

    function handlePointerLeave() {
      targetX = 0
      targetY = 0
      requestRender()
    }

    window.addEventListener('scroll', requestRender, { passive: true })
    window.addEventListener('resize', requestRender)
    hero.addEventListener('pointermove', handlePointerMove)
    hero.addEventListener('pointerleave', handlePointerLeave)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', requestRender)
      window.removeEventListener('resize', requestRender)
      hero.removeEventListener('pointermove', handlePointerMove)
      hero.removeEventListener('pointerleave', handlePointerLeave)
    }
  }, [])

  return (
    <div className={styles.container}>
      <div className={styles.hero} ref={heroRef}>
        <div className={styles.backgroundClip} aria-hidden>
          <div className={styles.background} ref={backgroundRef}>
            <Image
              fill
              priority
              alt=""
              sizes="100vw"
              src="/images/landing-hero-bg.webp"
            />
          </div>
        </div>
        <div className={styles.stage}>
          <div className={styles.waiter} ref={waiterRef} aria-hidden>
            {/* Not preloaded: the waiter is hidden on phones */}
            <Image
              alt=""
              width={862}
              height={947}
              src="/images/landing-hero-waiter.webp"
            />
          </div>
          <div className={styles.card} ref={cardRef}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
